import { computed, effect, inject, Injectable, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import {
  AuthProvider,
  BasketService,
  BREAKPOINTS,
  ConfigService,
  DEFAULT_CONFIG,
  GsDropdownMenuItem,
  InformationService,
  LanguageService,
  OpeningTimesService,
  ResponsiveAttribute,
  StyleService,
  SystemService
} from 'libs/shared/src';
import { AppLanguage } from 'libs/shared/src/models/applanguage/app-language';
import {
  CONFIG_HEADER_ACTION_LINK_TARGETS,
  CONFIG_HEADER_ACTIONS_NEEDS_INQUIRY,
  CONFIG_HEADER_ACTIONS_NEEDS_LOGIN,
  CONFIG_HEADER_ACTIONS_NEEDS_WEBSHOP,
  CONFIG_HEADER_ACTIONS_WITHOUT_LOGIN,
  ConfigHeaderAction,
  IConfigActionOptions,
  IConfigHeaderMainbar,
  IConfigHeaderMenubar,
  IConfigHeaderTopbar
} from 'libs/shared/src/models/configs';
import { MenuType } from 'libs/shared/src/models/structure/init';
import { PageService } from 'libs/shared/src/services/page/page.service';

/** Account actions the sidebar renders below the menu */
const SIDEBAR_ACTIONS = ['dashboard', 'watchlist', 'logout'] as const;
/** An account action of the sidebar */
export type SidebarAction = (typeof SIDEBAR_ACTIONS)[number];

@Injectable({
  providedIn: 'root'
})
export class HeaderService implements OnDestroy {
  // Services
  public readonly config = inject(ConfigService);
  public readonly infoService = inject(InformationService);
  public readonly system = inject(SystemService);
  private readonly authService = inject(AuthProvider);
  private readonly basketService = inject(BasketService);
  private readonly languageService = inject(LanguageService);
  private readonly openingTimesService = inject(OpeningTimesService);
  private readonly pageService = inject(PageService);
  private readonly styleService = inject(StyleService);

  // Properties & Signals
  /** Exposes the MenuType enum for template use or internal logic. */
  public readonly MenuType = MenuType;
  /** Exposes the default application configuration object. */
  public readonly DEFAULT_CONFIG = DEFAULT_CONFIG;
  /** Defines header actions available when the user is logged in. */
  public readonly CONFIG_HEADER_ACTIONS_NEEDS_LOGIN = CONFIG_HEADER_ACTIONS_NEEDS_LOGIN;
  /** Defines header actions available when the user is logged out. */
  public readonly CONFIG_HEADER_ACTIONS_WITHOUT_LOGIN = CONFIG_HEADER_ACTIONS_WITHOUT_LOGIN;
  /** Defines header actions that require the webshop module to be active. */
  public readonly CONFIG_HEADER_ACTIONS_NEEDS_WEBSHOP = CONFIG_HEADER_ACTIONS_NEEDS_WEBSHOP;
  /** Defines header actions that require an inquiry checkout page to exist (init.hasInquiry). */
  public readonly CONFIG_HEADER_ACTIONS_NEEDS_INQUIRY = CONFIG_HEADER_ACTIONS_NEEDS_INQUIRY;
  /** Base URL for flag images used in the header (e.g., for language selection). */
  public readonly flagurl = 'https://cordoba.blob.core.windows.net/uploads/flags/';
  /** All pages displayed in the dropdown */
  public readonly dropdownPages = signal<GsDropdownMenuItem[]>([]);
  /** LinkURLs for each action */
  public readonly actionLinks: WritableSignal<Record<string, string | undefined> | undefined> = signal(undefined);
  /** Computes and returns the configuration for the top bar section of the header. */
  public readonly topbarConfig: Signal<IConfigHeaderTopbar | undefined> = computed(() => this.config.config()?.header?.topbar);
  /** Computes and returns the processed topbar content with resolved data (only for 'custom' template) */
  public readonly topbar = computed(() => {
    const config = this.topbarConfig();
    const initData = this.infoService.init();

    if (!config?.active || config.template !== 'custom') return undefined;

    // Process custom content items (backend already filters inactive items)
    const customItems = (initData?.topbar ?? [])
      .sort((a, b) => {
        // Sort by index
        const orderA = a.index ?? 0;
        const orderB = b.index ?? 0;
        return orderA - orderB;
      })
      .map((item) => ({
        type: 'containerItem' as const,
        content: item.title ?? '',
        url: item.details,
        cssClasses: undefined,
        containerItem: item
      }));

    return {
      config,
      customItems
    };
  });
  /**
   * Whether the topbar should be displayed.
   * Hidden when the topbar is inactive, when the 'custom' template is selected but has no content (no items with text),
   * or when the 'openingHours' template is selected but no opening times are maintained.
   */
  public readonly showTopbar: Signal<boolean> = computed(() => {
    const config = this.topbarConfig();
    if (!config?.active) return false;
    if (config.template === 'custom') {
      return (this.topbar()?.customItems ?? []).some((item) => (item.content?.trim().length ?? 0) > 0);
    }
    if (config.template === 'openingHours') return this.hasOpeningTimes();
    return true;
  });
  /** The measured rendered height (in px) of the topbar slot. */
  public readonly topbarHeight: WritableSignal<number> = signal(0);
  /**
   * The height (in px) the header covers at the top of the viewport once pinned, `0` while it is not
   * sticky. Anchored page components offset their `scroll-margin-top` by it so a jump mark does not
   * land underneath the pinned header.
   */
  public readonly stickyHeight: WritableSignal<number> = signal(0);
  /** Computes and returns the configuration for the menu bar section of the header. */
  public readonly menubarConfig: Signal<IConfigHeaderMenubar | undefined> = computed(() => this.config.config()?.header?.menubar);
  /**
   * Whether today's opening hours should be shown in the mobile sidebar (burger) menu.
   * Defaults to true, but stays hidden when no opening times are maintained.
   */
  public readonly showOpeningHoursInSidebar: Signal<boolean> = computed(
    () =>
      (this.menubarConfig()?.showOpeningHoursInSidebar ?? DEFAULT_CONFIG.header?.menubar?.showOpeningHoursInSidebar ?? true) &&
      this.hasOpeningTimes()
  );
  /** Computes and returns the configuration for the main bar section of the header. */
  public readonly mainbarConfig: Signal<IConfigHeaderMainbar | undefined> = computed(() => this.config.config()?.header?.mainbar);
  /**
   * Effective header actions: the default actions as base, overridden per key by the channel config.
   * The config-service merge is shallow (`header ?? DEFAULT_CONFIG.header`), so a channel that carries
   * any header config would otherwise never see newly shipped standard actions (e.g. the Anfrageliste).
   * Merging here surfaces them everywhere while letting an explicit per-action config (icon, active,
   * orderIndex) win.
   */
  public readonly mainbarActions = computed<Partial<Record<ConfigHeaderAction, IConfigActionOptions>>>(() => {
    const configActions = this.mainbarConfig()?.actions?.actions ?? {};
    const defaultActions = DEFAULT_CONFIG.header?.mainbar?.actions?.actions ?? {};
    return { ...defaultActions, ...configActions };
  });
  /**
   * Which account actions the sidebar renders (dashboard, watchlist, logout): the non-responsive
   * gates — login state, configured link target, webshop module. Logout needs no link target, it runs
   * the same action as the mainbar ({@link performAction}). The per-breakpoint `active` toggle is
   * applied on top in CSS.
   */
  public readonly sidebarActionsShown: Signal<Record<SidebarAction, boolean>> = computed(() => {
    const linkTargets = this.infoService.init()?.linkTargets;
    const isLoggedIn = this.isLoggedIn();
    return {
      dashboard: isLoggedIn && !!linkTargets?.dashboard,
      watchlist: !!linkTargets?.watchlist && this.system.isWebshop,
      logout: isLoggedIn
    };
  });
  /**
   * Per-breakpoint visibility of the whole sidebar action block: true wherever at least one rendered
   * action is active. Without it the block's border and spacing would stay behind empty at
   * breakpoints where CSS hides all three actions.
   */
  public readonly sidebarActionsVisible: Signal<ResponsiveAttribute<boolean>> = computed(() => {
    const shown = this.sidebarActionsShown();
    const actives = SIDEBAR_ACTIONS.filter((action) => shown[action]).map((action) => this.sidebarActionActive(action));
    const visible = {} as ResponsiveAttribute<boolean>;
    for (const breakpoint of BREAKPOINTS) {
      visible[breakpoint] = actives.some((active) => this.styleService.getResponsiveValueAt(active, breakpoint) ?? false);
    }
    return visible;
  });
  /** Computes and returns the company information from the initialization data. */
  public readonly company = computed(() => this.infoService.init()?.companyInfo);
  /**
   * Whether the company has opening times maintained in the backend.
   * Stays true until the company is loaded, so the loading state does not make the opening hours flicker.
   */
  public readonly hasOpeningTimes: Signal<boolean> = computed(() => {
    const company = this.company();
    if (!company) return true;

    return this.openingTimesService.hasOpeningTimes(company.openingTimesInfos, company.specialOpeningTimesInfo);
  });
  /** Computes today's opening-time text from opening-times arrays delivered by the API. */
  public readonly todaysOpeningTime = computed(() => {
    const company = this.company();
    if (!company) return undefined;

    return this.openingTimesService.getTodaysOpeningTimeTextForEntries(company.openingTimesInfos, company.specialOpeningTimesInfo);
  });
  /** Computes and returns the current user login status (true if logged in AND no guest, false otherwise). */
  public readonly isLoggedIn = computed(() => {
    const loggedIn = this.authService.isLoggedIn();
    const isGuest = this.basketService.isGuest;
    return loggedIn && !isGuest;
  });
  /**
   * Determines if the header should use a container (e.g., a fixed-width content area)
   * based on the configured header layout.
   */
  public readonly useContainer = computed(() => {
    const layout = this.config.config()?.header?.layout;
    return layout === 'LayoutContent';
  });
  /** Available languages for the current page from the backend. Returns undefined if empty or not set. */
  public readonly pageLanguages: Signal<Partial<Record<AppLanguage, string>> | undefined> = computed(() => {
    const languages = this.pageService.pageSignal()?.languages;
    if (!languages || Object.keys(languages).length === 0) return undefined;
    return languages;
  });

  // Language Menu State
  /** Controls visibility of the desktop language dropdown */
  public readonly langMenuOpen: WritableSignal<boolean> = signal(false);
  /** Controls visibility of the sidebar language submenu */
  public readonly langSideMenuOpen: WritableSignal<boolean> = signal(false);

  /**
   * Computes the target link URL for the header logo.
   * Prioritizes a specific configured link, falls back to the home link ('/'),
   * or uses '/' if configured to explicitly target the home link.
   */
  public readonly logoLink: Signal<string> = computed<string>(() => {
    const mainbar = this.mainbarConfig();
    if (mainbar?.logo?.useHomeLinkTarget) return '/';
    if (mainbar?.logo?.linkUrl && mainbar.logo.linkUrl.length > 0) return mainbar.logo.linkUrl;
    return '/'; // Default fallback
  });
  /** Timeout handle for delayed closing of the desktop language dropdown */
  private langMenuTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    effect(() => {
      this.openingTimesService.setStoreTimeZone(this.company()?.timeZone);
    });

    effect(() => {
      const initData = this.infoService.init();
      if (initData) {
        console.debug('[HeaderService] Init Data received', { timestamp: new Date().toISOString() });
      }

      // Initialize dropdown pages
      if (initData?.menus?.['cs4-menu']) {
        this.dropdownPages.set(this.infoService.generateMenu(initData.menus['cs4-menu']));
      }

      // Initialize action links
      const linkTargets: Record<string, string> | undefined = initData?.linkTargets;
      let links: Record<string, string> = {};
      Object.entries(this.mainbarActions()).forEach(([actionName]) => {
        const linkTargetKey = CONFIG_HEADER_ACTION_LINK_TARGETS[actionName as ConfigHeaderAction] ?? actionName;
        if (linkTargets && linkTargetKey in linkTargets) {
          links[actionName] = linkTargets[linkTargetKey];
        } else if (initData) {
          // Before the init data arrives there are no link targets at all — only a missing entry afterwards is worth warning about.
          console.warn(
            `[Header] Action "${actionName}" is configured but has no link target. Please configure it in template config under linkTargets.`
          );
        }
      });
      this.actionLinks.set(links as Record<ConfigHeaderAction, string | undefined>);
    });
  }

  // Methods
  /**
   * The configured (responsive) `active` toggle of a sidebar account action, falling back to the
   * default config per action — the sidebar mirrors the desktop mainbar.
   * @param action The sidebar action
   * @returns The toggle per breakpoint, or undefined when neither config sets one
   */
  public sidebarActionActive(action: SidebarAction): ResponsiveAttribute<boolean> | undefined {
    return this.mainbarConfig()?.actions?.actions?.[action]?.active ?? DEFAULT_CONFIG.header?.mainbar?.actions?.actions?.[action]?.active;
  }

  /**
   * Executes a specific header action based on its name.
   * Currently handles the 'logout' action, which performs a logout and redirects if necessary.
   *
   * @param name The unique name of the action to perform (e.g., 'logout').
   * @returns true if the action was recognized and performed, false otherwise.
   */
  public performAction(name: string): boolean {
    if (name === 'logout') {
      this.authService.logout(true).subscribe({
        next: (data) => {
          if (data.redirectUrl) {
            this.system.openLink(data.redirectUrl);
          }
        },
        error: (error) => console.error(error)
      });
      return true;
    }
    return false;
  }

  /**
   * Generates a simple, unique tracking ID string for a dropdown menu item.
   * This can be used for tracking, unique keys in Angular templates, or debugging.
   * @description iD is no primary-key here... Dont ask.. ✨The Backend✨
   * @param item The dropdown menu item object.
   * @returns A concatenated string of the item's label, ID, and link URL.
   */
  public trackHeaderMenu(item: GsDropdownMenuItem): string {
    return item.label + item.iD + item.linkUrl;
  }

  ngOnDestroy(): void {
    if (this.langMenuTimeout) {
      clearTimeout(this.langMenuTimeout);
    }
  }

  /**
   * Opens the desktop language dropdown and clears any pending close timeout.
   */
  public onLangMenuEnter(): void {
    if (this.langMenuTimeout) {
      clearTimeout(this.langMenuTimeout);
      this.langMenuTimeout = undefined;
    }
    this.langMenuOpen.set(true);
  }

  /**
   * Toggles the desktop language dropdown open/closed and clears any pending close timeout.
   */
  public toggleLangMenu(): void {
    if (this.langMenuTimeout) {
      clearTimeout(this.langMenuTimeout);
      this.langMenuTimeout = undefined;
    }
    this.langMenuOpen.update((open) => !open);
  }

  /**
   * Closes the desktop language dropdown after a short delay.
   */
  public onLangMenuLeave(): void {
    this.langMenuTimeout = setTimeout(() => {
      this.langMenuOpen.set(false);
    }, 150);
  }

  /**
   * Closes the desktop language dropdown when focus leaves the wrapper.
   * @param event The focus event from the wrapper element
   */
  public onLangMenuFocusOut(event: FocusEvent): void {
    const wrapper = event.currentTarget as HTMLElement;
    if (!wrapper.contains(event.relatedTarget as Node)) {
      this.langMenuOpen.set(false);
    }
  }

  /**
   * Closes the desktop language dropdown on Escape key and returns focus to the trigger button.
   * @param event The keyboard event from the wrapper element
   */
  public onLangMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.langMenuOpen()) {
      this.langMenuOpen.set(false);
      const trigger = (event.currentTarget as HTMLElement).querySelector<HTMLElement>('button');
      trigger?.focus();
    }
  }

  /**
   * Changes the page language by updating the language header and reloading the page.
   * Uses setLanguage() to ensure the full AppLanguage code (e.g., 'de-DE') is stored
   * and sent as the language header via the auth interceptor.
   * @param lang The AppLanguage code to switch to (e.g., 'de-DE', 'en-US')
   */
  public changeLanguage(lang: AppLanguage): void {
    this.languageService.setLanguage(lang);
    if (this.system.browser) {
      this.system.reloadPage();
    }
  }

  /**
   * Checks if a menu item or any of its nested children matches the given URL.
   * Used to highlight parent items when a child page is active.
   * @param menuItem The menu item to check (including its nested items)
   * @param currentUrl The current relative URL to match against
   * @returns true if the item itself or any nested child matches the URL
   */
  public isMenuItemOrChildActive(menuItem: GsDropdownMenuItem, currentUrl: string): boolean {
    if (menuItem.linkUrl === currentUrl) return true;
    return menuItem.items?.some((child) => this.isMenuItemOrChildActive(child, currentUrl)) ?? false;
  }

  /**
   * Handles the keyboard event on a dropdown menu item (Enter to open).
   * @param dropdownComp The dropdown component
   * @param event The keyboard event
   * @param menuItem The dropdown item being focused.
   */
  public handleDropdownKeydown(dropdownComp: any, event: KeyboardEvent, menuItem: GsDropdownMenuItem): void {
    if (event.key === 'Enter' && menuItem.items.length > 0 && !dropdownComp.opened()) {
      event.preventDefault();
      dropdownComp.toggleDropdown(true, event, undefined, true);
    }
  }
}
